/**
 * Radio group, checkbox and toggle.
 *
 * Single-select is unambiguous here: exactly one radio can be filled. The
 * desktop payment design showed two methods checked at once (gap G-23), which
 * these semantics make impossible.
 */

import React, { memo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { colors, radius, sizing, space } from '../../theme';
import { Text } from '../common/Text';

export interface Option<T extends string> {
  value: T;
  label: string;
  description?: string;
  /** Rendered on the left, e.g. a provider logo. */
  leading?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<T extends string> {
  options: Option<T>[];
  value: T | null;
  onChange: (value: T) => void;
  label?: string;
  error?: string | null;
  testID?: string;
}

export function RadioGroup<T extends string>({
  options,
  value,
  onChange,
  label,
  error,
  testID = 'radio-group',
}: RadioGroupProps<T>) {
  return (
    <View testID={testID} accessibilityRole="radiogroup" accessibilityLabel={label}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            testID={`${testID}-${option.value}`}
            onPress={() => !option.disabled && onChange(option.value)}
            disabled={option.disabled}
            accessibilityRole="radio"
            accessibilityState={{ selected, disabled: option.disabled }}
            accessibilityLabel={option.label}
            accessibilityHint={option.description}
            style={[
              styles.row,
              index > 0 && styles.rowDivided,
              option.disabled && styles.disabled,
            ]}
          >
            {option.leading ? <View style={styles.leading}>{option.leading}</View> : null}

            <View style={styles.rowBody}>
              <Text variant="body">{option.label}</Text>
              {option.description ? (
                <Text variant="caption" tone="secondary">
                  {option.description}
                </Text>
              ) : null}
            </View>

            <View style={[styles.radio, selected && styles.radioSelected]}>
              {selected ? <View style={styles.radioDot} /> : null}
            </View>
          </Pressable>
        );
      })}

      {error ? (
        <Text variant="caption" tone="danger" accessibilityLiveRegion="polite" style={styles.error}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  testID?: string;
}

export const Checkbox = memo(function Checkbox({
  checked,
  onChange,
  label,
  description,
  testID = 'checkbox',
}: CheckboxProps) {
  return (
    <Pressable
      testID={testID}
      onPress={() => onChange(!checked)}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={label}
      accessibilityHint={description}
      style={styles.checkboxRow}
    >
      <View style={[styles.box, checked && styles.boxChecked]}>
        {checked ? <View style={styles.tick} /> : null}
      </View>
      <View style={styles.rowBody}>
        <Text variant="body">{label}</Text>
        {description ? (
          <Text variant="caption" tone="secondary">
            {description}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
});

export const Toggle = memo(function Toggle({
  value,
  onChange,
  label,
  description,
  testID = 'toggle',
}: {
  value: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
  testID?: string;
}) {
  return (
    <Pressable
      testID={testID}
      onPress={() => onChange(!value)}
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      accessibilityLabel={label}
      accessibilityHint={description}
      style={styles.toggleRow}
    >
      <View style={styles.rowBody}>
        <Text variant="body">{label}</Text>
        {description ? (
          <Text variant="caption" tone="secondary">
            {description}
          </Text>
        ) : null}
      </View>
      <View style={[styles.track, value && styles.trackOn]}>
        <View style={[styles.thumb, value && styles.thumbOn]} />
      </View>
    </Pressable>
  );
});

const RADIO = 22;

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    paddingVertical: space.md,
    minHeight: sizing.touchTarget,
  },
  rowDivided: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },
  rowBody: {
    flex: 1,
    gap: space.xxs,
  },
  leading: {
    width: 40,
    alignItems: 'center',
  },
  disabled: {
    opacity: 0.5,
  },
  radio: {
    width: RADIO,
    height: RADIO,
    borderRadius: radius.full,
    borderWidth: 2,
    borderColor: colors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: {
    borderColor: colors.bg.brand,
  },
  radioDot: {
    width: RADIO / 2,
    height: RADIO / 2,
    borderRadius: radius.full,
    backgroundColor: colors.bg.brand,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: space.md,
    paddingVertical: space.sm,
    minHeight: sizing.touchTarget,
  },
  box: {
    width: 24,
    height: 24,
    borderRadius: radius.sm,
    borderWidth: 2,
    borderColor: colors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  boxChecked: {
    backgroundColor: colors.bg.brand,
    borderColor: colors.bg.brand,
  },
  tick: {
    width: 7,
    height: 12,
    borderRightWidth: 2.5,
    borderBottomWidth: 2.5,
    borderColor: colors.text.onBrand,
    transform: [{ rotate: '45deg' }],
    marginTop: -3,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    paddingVertical: space.md,
    minHeight: sizing.touchTarget,
  },
  track: {
    width: 48,
    height: 28,
    borderRadius: radius.full,
    backgroundColor: colors.border.strong,
    padding: 3,
    justifyContent: 'center',
  },
  trackOn: {
    backgroundColor: colors.bg.brand,
  },
  thumb: {
    width: 22,
    height: 22,
    borderRadius: radius.full,
    backgroundColor: colors.bg.surface,
  },
  thumbOn: {
    alignSelf: 'flex-end',
  },
  error: {
    marginTop: space.xs,
  },
});
